/*
 * Boho Mantra — Shopify Inbox chat trigger
 * ---------------------------------------------------------------------------
 * Opens the Inbox chat panel in place, so an in-page "Need help?" link no
 * longer has to navigate to `?chat` and reload the whole page.
 *
 * Why this exists: Inbox used to auto-open when the URL carried `?chat`, and
 * the widget rendered as <inbox-online-store-chat>. Neither is true now — the
 * widget renders as <shopify-chat> (an "AgentShell" element exposing .show())
 * and ignores the query string, so every `?chat` link silently stopped working.
 *
 * Anything with [data-chat-open] becomes a trigger. Keep those triggers as real
 * anchors pointing at a no-JS fallback (e.g. the contact page): if Inbox is
 * blocked, uninstalled, or never mounts, the click falls through to the link.
 */
(function () {
  'use strict';

  // Newest first — the legacy element is kept so an Inbox rollback still works.
  var WIDGET_SELECTOR = 'shopify-chat, inbox-online-store-chat';

  // Inbox injects this into the server-rendered HTML when the app embed is on.
  // Its presence is what tells us a widget is *coming*, before it has mounted.
  var EMBED_MARKER = '#shopify-chat-app-embed-data';

  // The widget mounts a few seconds after load, so a click can land first.
  // This ceiling only bites when Inbox is genuinely blocked; keep it short
  // enough that the fallback link does not feel like a dead click.
  var MOUNT_TIMEOUT_MS = 6000;
  var OPEN_RETRY_MS = 150;
  var OPEN_MAX_TRIES = 12;

  function inboxInstalled() {
    return document.querySelector(EMBED_MARKER) !== null;
  }

  /* Ask the widget to open. The element can be in the DOM before its custom
   * element definition has upgraded it, and its shadow root may not be
   * populated yet, so retry briefly before handing over to `onFailure`.
   * Deliberately does not wait on customElements.whenDefined(): that promise
   * never settles if the element is never registered, which would strand the
   * click with no chat and no fallback. */
  function openWidget(el, onFailure, tries) {
    tries = tries || 0;

    if (typeof el.show === 'function') {
      el.show();
      return;
    }

    // Legacy path: no public method, but the activator lives in an open shadow root.
    var activator =
      el.shadowRoot && el.shadowRoot.querySelector('button[part="activator"], button.activator, button');
    if (activator) {
      activator.click();
      return;
    }

    if (tries < OPEN_MAX_TRIES) {
      setTimeout(function () {
        openWidget(el, onFailure, tries + 1);
      }, OPEN_RETRY_MS);
      return;
    }

    if (onFailure) onFailure();
  }

  /* Resolve the widget element, waiting for it to mount if it has not yet. */
  function whenWidgetReady(callback) {
    var existing = document.querySelector(WIDGET_SELECTOR);
    if (existing) {
      callback(existing);
      return;
    }

    var timer;
    var observer = new MutationObserver(function () {
      var el = document.querySelector(WIDGET_SELECTOR);
      if (!el) return;
      observer.disconnect();
      clearTimeout(timer);
      callback(el);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    timer = setTimeout(function () {
      observer.disconnect();
      callback(null);
    }, MOUNT_TIMEOUT_MS);
  }

  /* Open the chat. `onFailure` runs if the widget never turns up. */
  function openChat(onFailure) {
    whenWidgetReady(function (el) {
      if (!el) {
        if (onFailure) onFailure();
        return;
      }
      openWidget(el, onFailure);
    });
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var trigger = target.closest('[data-chat-open]');
    if (!trigger) return;

    // No Inbox on this page — let the anchor do its normal thing.
    if (!inboxInstalled()) return;

    event.preventDefault();

    var fallbackUrl = trigger.getAttribute('href');
    openChat(function () {
      if (fallbackUrl) window.location.href = fallbackUrl;
    });
  });

  /* Keep inbound `?chat` / `#chat` links working (emails, old links, QR codes).
   * The parameter is dropped from the URL afterwards so a refresh or a shared
   * copy of the link does not reopen the panel. */
  function handleDeepLink() {
    var params = new URLSearchParams(window.location.search);
    var wanted = params.has('chat') || window.location.hash === '#chat';
    if (!wanted || !inboxInstalled()) return;

    if (params.has('chat')) {
      params.delete('chat');
      var query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (query ? '?' + query : '') + window.location.hash
      );
    }

    openChat(null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleDeepLink);
  } else {
    handleDeepLink();
  }
})();
